<template>
  <div v-if="isCurrentItemOwner && hasRequestContentResponse && requestContentItem" class="request-details-card" data-testid="request-details-card">
    <p class="request-details-card__title">Request Details</p>

    <div v-if="requestContentItem.requestLink" class="request-details-card__row">
      <span class="request-details-card__label">Request Link</span>
      <a
        class="request-details-card__link"
        :href="requestContentItem.requestLink"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="request-details-card-link"
        >{{ requestContentItem.requestLink }}</a
      >
    </div>

    <div v-if="requestContentItem.collectionId" class="request-details-card__row">
      <span class="request-details-card__label">Collection</span>
      <a class="request-details-card__link" :href="`/collections/${requestContentItem.collectionId}`" data-testid="request-details-card-collection">
        {{ requestContentItem.collectionName || 'View collection' }}
      </a>
    </div>

    <div class="request-details-card__row">
      <span class="request-details-card__label">Created</span>
      <span class="request-details-card__value">{{ formatDate(requestContentItem.createdAt) }}</span>
    </div>

    <!-- The card only renders for the item owner (see the root v-if), and the
         item owner is the reply uploader — i.e. the respondent. There's no
         separate "responder" record to look up; it's whoever is logged in. -->
    <div v-if="respondentName" class="request-details-card__row">
      <span class="request-details-card__label">Respondent</span>
      <span class="request-details-card__value">{{ respondentName }}</span>
    </div>

    <div class="request-details-card__row">
      <span class="request-details-card__label">Status</span>
      <span class="request-details-card__badge" :class="`request-details-card__badge--${requestContentItem.status}`">
        {{ formatStatus(requestContentItem.status) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useRequestContentInfoPage } from '../composables/useRequestContentInfoPage';
import { useUser } from '../composables/useUser';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useUserStore } from '@/store/useUserStore';

// MetadataSection mounts RequestContentInfo alongside this card and owns the
// shared request-content fetch; this card only consumes that reactive result.
const { requestContentItem } = useRequestContentInfoPage();
const { isCurrentItemOwner } = useUser();
const { hasRequestContentResponse } = storeToRefs(useViewerItemStore());
const { name: respondentName } = storeToRefs(useUserStore());

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatStatus = (status: string) => {
  switch (status) {
    case 'active':
      return 'ON';
    case 'expired':
      return 'Expired';
    default:
      return 'OFF';
  }
};
</script>

<style scoped>
.request-details-card {
  @apply flex flex-col rounded-10 border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800;
}

.request-details-card__title {
  @apply px-4 pt-4 pb-2.5 text-xs font-bold uppercase text-zinc-900 dark:text-zinc-100;
}

.request-details-card__row {
  @apply flex items-center gap-2 px-4 py-3 text-sm border-t border-slate-100 dark:border-zinc-700/60;
}

.request-details-card__label {
  @apply flex-shrink-0 w-[120px] font-bold text-zinc-600 dark:text-zinc-400;
}

.request-details-card__value {
  @apply text-zinc-900 dark:text-zinc-100;
}

.request-details-card__link {
  @apply text-blue-500 truncate;
}

.request-details-card__badge {
  @apply inline-flex items-center px-2 py-0.5 rounded-10 text-sm font-semibold;
}

.request-details-card__badge--active {
  @apply bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400;
}

.request-details-card__badge--inactive {
  @apply bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-400;
}

.request-details-card__badge--expired {
  @apply bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400;
}
</style>
